import axios, { AxiosResponse } from 'axios'
import { csrfToken } from '../csrfToken'

const parseFileUrlFromXml = (xml: string): string | undefined => {
  const matches = xml.match(/<Location>(.+?)<\/Location>/)

  if (matches) {
    return matches[1]
  }

  return undefined
}

interface UploadFields {
  [key: string]: string
}

const buildFormData = (uploadFields: UploadFields, file: File): FormData => {
  const formData = new FormData()

  Object.keys(uploadFields).forEach((key) => {
    formData.append(key, uploadFields[key])
  })

  formData.append('file', file)
  return formData
}

/**
 * Uploads a file and resolves to its URL. The file goes to the app, which
 * writes it to every image store (S3 and Bunny Storage). An endpoint that
 * still hands back a presigned S3 POST instead (private ID documents) is
 * followed as before.
 */
export const uploadFileToS3 = async (
  uploadPath: string,
  file: File,
  localiseUrl: (path: string, options: { localeAsParam: boolean }) => string = (
    path
  ) => path
): Promise<string | undefined> => {
  const upload = new FormData()

  upload.append('file', file)
  upload.append('authenticity_token', csrfToken)

  const { data } = await axios.post<
    { location: string } | { url: string; upload_fields: UploadFields }
  >(localiseUrl(uploadPath, { localeAsParam: true }), upload)

  if ('location' in data) return data.location

  const uploadedFileData: AxiosResponse<string> = await axios.post(
    localiseUrl(data.url, { localeAsParam: true }),
    buildFormData(data.upload_fields, file)
  )

  return parseFileUrlFromXml(uploadedFileData.data)
}

export const fileSizeString = (fileSizeInBytes: number): string => {
  let fileSizeInB = fileSizeInBytes
  let i = -1
  const byteUnits = [' kB', ' MB', ' GB', ' TB', 'PB', 'EB', 'ZB', 'YB']

  do {
    fileSizeInB /= 1024
    i += 1
  } while (fileSizeInB > 1024)

  return `${Math.max(fileSizeInB, 0.1).toFixed(1)}${byteUnits[i]}`
}
